"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useAuth } from "@/contexts/auth-context";
import {
  listLibrary,
  listPlaylists,
  type PlaylistSummary,
} from "@/lib/api/api-client";
import {
  EMPTY_LIBRARY_COUNTS,
  LIBRARY_COUNT_FILTERS,
  LIBRARY_COUNT_KEYS,
  type LibraryCountKey,
  type LibraryCounts,
} from "@/lib/library/library-views";

/**
 * Ce que toute la bibliothèque a besoin de savoir d'elle-même : combien de
 * morceaux dans chaque vue, quel est le dernier travaillé, et quelles playlists
 * existent.
 *
 * # Pourquoi un seul propriétaire
 *
 * Trois surfaces posent les mêmes questions : la carte du répertoire (les
 * comptes des deux lignes et la liste des playlists), la rangée de pastilles de
 * « Mes morceaux » (les quatre comptes), et le popover « Ajouter à une
 * playlist » (la liste, sur chaque carte). Chacune les demandant pour elle,
 * ouvrir « Mes morceaux » aurait déclenché les mêmes appels deux fois, et
 * ouvrir un menu un troisième — sur une donnée qui ne bouge que quand on la
 * change soi-même.
 *
 * Monté par `LibraryShell`, donc au-dessus de toutes les pages de la
 * bibliothèque : les comptes survivent au passage d'une page à l'autre, et la
 * sidebar ne clignote pas à chaque navigation.
 *
 * # Cinq appels minuscules plutôt qu'une route de résumé
 *
 * Chaque compte est un `limit: 1` : le serveur rend `meta.total` et une seule
 * ligne. C'est ce qui évite d'inventer un `GET /api/library/summary` pour
 * quatre entiers, et ça donne au passage le titre du dernier morceau travaillé,
 * dont la carte du répertoire a besoin.
 *
 * # Le rafraîchissement est explicite
 *
 * Ajouter, retirer, créer ou supprimer appelle `refresh()`. Pas de
 * réinterrogation périodique et pas d'invalidation devinée : la bibliothèque
 * d'une personne ne change que sous ses propres doigts.
 */

export type LibraryOverview = {
  /** Le premier chargement est passé. Faux pour un anonyme, qui n'en a pas. */
  readonly ready: boolean;
  readonly counts: LibraryCounts;
  /**
   * Le dernier morceau qui porte un état de travail, `null` s'il n'y en a
   * aucun. La carte du répertoire en fait l'indice de « Là où tu t'es arrêté ».
   */
  readonly resumeTitle: string | null;
  readonly playlists: readonly PlaylistSummary[];
  /** Relit tout. À appeler après un geste qui change la bibliothèque. */
  readonly refresh: () => void;
};

const EMPTY: Omit<LibraryOverview, "refresh"> = {
  ready: false,
  counts: EMPTY_LIBRARY_COUNTS,
  resumeTitle: null,
  playlists: [],
};

const LibraryOverviewContext = createContext<LibraryOverview | null>(null);

export function LibraryOverviewProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth();
  const [state, setState] = useState(EMPTY);
  /** Incrémenté par `refresh()`. Une dépendance d'effet, pas un déclencheur. */
  const [revision, setRevision] = useState(0);

  const refresh = useCallback(() => setRevision((value) => value + 1), []);

  useEffect(() => {
    if (!isAuthenticated) {
      setState(EMPTY);

      return;
    }

    let cancelled = false;

    void Promise.all([
      Promise.all(
        LIBRARY_COUNT_KEYS.map((key) =>
          listLibrary({ filter: LIBRARY_COUNT_FILTERS[key], limit: 1 }),
        ),
      ),
      listPlaylists(),
    ])
      .then(([pages, playlists]) => {
        if (cancelled) {
          return;
        }

        const counts = {} as Record<LibraryCountKey, number>;

        LIBRARY_COUNT_KEYS.forEach((key, index) => {
          counts[key] = pages[index].meta.total;
        });

        setState({
          ready: true,
          counts,
          // La première ligne de `practiced`, triée par dernière ouverture :
          // c'est bien « là où tu t'es arrêté », et pas « le dernier morceau
          // ouvert », qui pourrait être une fiche entrouverte hier soir.
          resumeTitle:
            pages[LIBRARY_COUNT_KEYS.indexOf("practiced")].items[0]?.title ??
            null,
          playlists: playlists.items,
        });
      })
      .catch(() => {
        /*
          Décoratif de bout en bout : ces chiffres complètent des lignes qui se
          lisent sans eux. Une panne fait donc disparaître les comptes, jamais
          la navigation — et surtout jamais un message d'erreur dans une
          sidebar, où personne ne saurait quoi en faire.
        */
      });

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, revision]);

  const value = useMemo<LibraryOverview>(
    () => ({ ...state, refresh }),
    [state, refresh],
  );

  return (
    <LibraryOverviewContext.Provider value={value}>
      {children}
    </LibraryOverviewContext.Provider>
  );
}

export function useLibraryOverview(): LibraryOverview {
  const context = useContext(LibraryOverviewContext);

  if (context === null) {
    throw new Error(
      "useLibraryOverview doit être appelé sous un LibraryOverviewProvider",
    );
  }

  return context;
}
